import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContactSection } from '../../components/ContactSection';

/* ICONS (inline SVG to avoid import deps) */
const Icon = ({ d, size = 20, className = "", strokeWidth = 1.5, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={className} style={style}>
    <path d={d} />
  </svg>
);

const icons = {
  globe: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 0c-2.5 3-4 6.5-4 10s1.5 7 4 10m0-20c2.5 3 4 6.5 4 10s-1.5 7-4 10M2 12h20",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  zap: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  credit: "M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6zm0 5h20",
  clock: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 6v4l3 3",
  alert: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z",
  package: "M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m22 0v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  dollar: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  network: "M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm4 6H14V2",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zm11-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  layers: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  monitor: "M20 3H4a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8 21h8m-4-4v4",
  target: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-6a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  refresh: "M23 4v6h-6M1 20v-6h6m-.93-9A10 10 0 0 1 21.93 12a10 10 0 0 1-17.86 7",
  msg: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  help: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z",
  arrow: "M5 12h14m-7-7 7 7-7 7",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8.188-3A8.953 8.953 0 0 0 21 12a9 9 0 0 0-.812-3.766l-2.455.818A6.5 6.5 0 0 1 18 12a6.5 6.5 0 0 1-.267 1.948l2.455.818zM12 3a9 9 0 0 0-3.766.812l.818 2.455A6.5 6.5 0 0 1 12 6a6.5 6.5 0 0 1 1.948.267l.818-2.455A9 9 0 0 0 12 3z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  bar: "M12 20V10m6 10V4M6 20v-4",
  trending: "M23 6l-9.5 9.5-5-5L1 18",
};

/* Particle Canvas */
const ParticleCanvas = ({ color = "#ff5d22", count = 70, maxDist = 130 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w, h, pts, raf;
    const init = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 1.8 + 0.5,
        pulse: Math.random() * Math.PI * 2,
      }));
    };
    const hex2rgb = (h) => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
    const [r,g,b] = hex2rgb(color);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.02;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < maxDist) {
            const alpha = (1 - d / maxDist) * 0.25;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx.lineWidth = 0.7;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + 0.3 * Math.sin(p.pulse));
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3);
        grad.addColorStop(0, `rgba(${r},${g},${b},0.9)`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, pr * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [color, count, maxDist]);
  return <canvas ref={ref} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
};

/* 3D Grid Floor */
const GridFloor = ({ opacity = 0.07 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w, h, raf, t = 0;
    const init = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    const draw = () => {
      t += 0.003;
      ctx.clearRect(0, 0, w, h);
      const cols = 20, rows = 14;
      const cw = w / cols, ch = h / rows;
      for (let i = 0; i <= cols; i++) {
        const x = i * cw;
        const wave = Math.sin(i * 0.4 + t) * 8;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.5 + 0.5 * Math.sin(i * 0.3 + t))})`;
        ctx.lineWidth = 0.5;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h + wave);
        ctx.stroke();
      }
      for (let j = 0; j <= rows; j++) {
        const y = j * ch;
        const wave = Math.sin(j * 0.5 + t) * 5;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.5 + 0.5 * Math.cos(j * 0.3 + t))})`;
        ctx.lineWidth = 0.5;
        ctx.moveTo(0, y + wave);
        ctx.lineTo(w, y + wave);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [opacity]);
  return <canvas ref={ref} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
};

/* Orb */
const Orb = ({ style, color, size, animDur, animDelay = 0 }) => (
  <div style={{
    position:"absolute", borderRadius:"50%", filter:`blur(${size*0.35}px)`,
    width:size, height:size, background:color, opacity:0.15,
    animation:`orbFloat ${animDur}s ease-in-out ${animDelay}s infinite`,
    ...style,
  }} />
);

/* Reveal wrapper */
const Reveal = ({ children, delay = 0, style = {}, className = "" }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0, transform: vis ? "translateY(0) scale(1)" : "translateY(32px) scale(0.97)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* Tilt card */
const TiltCard = ({ children, className = "", style = {} }) => {
  const ref = useRef(null);
  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 14;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -14;
    ref.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${y}deg) translateY(-6px)`;
  };
  const handleLeave = () => { ref.current.style.transform = "perspective(800px) rotateY(0) rotateX(0) translateY(0)"; };
  return (
    <div ref={ref} className={className} style={{ transition:"transform 0.3s ease", transformStyle:"preserve-3d", ...style }}
      onMouseMove={handleMove} onMouseLeave={handleLeave}>
      {children}
    </div>
  );
};

/* FAQ Item */
const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={idx * 0.08}>
      <div onClick={() => setOpen(!open)} style={{
        background: open ? "linear-gradient(135deg,#0f0f0f,#1a1a1a)" : "#fff",
        border: `1px solid ${open ? "rgba(227,30,36,0.5)" : "#e8e8e8"}`,
        borderRadius:12, padding:"16px 20px", cursor:"pointer",
        transition:"all 0.35s ease", boxShadow: open ? "0 8px 40px rgba(227,30,36,0.15)" : "0 2px 12px rgba(0,0,0,0.05)",
        marginBottom:10,
      }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:16 }}>
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, color: open ? "#fff" : "#0f0f0f", letterSpacing:"-0.01em" }}>
            {q}
          </span>
          <div style={{
            width:28, height:28, borderRadius:"50%", background: open ? "#ff5d22" : "#f4f4f4",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
            transition:"all 0.3s ease", transform: open ? "rotate(45deg)" : "rotate(0)",
          }}>
            <Icon d="M12 5v14M5 12h14" size={12} className="" style={{ color: open ? "#fff" : "#666" }} />
          </div>
        </div>
        <div style={{
          overflow:"hidden", maxHeight: open ? 150 : 0, transition:"max-height 0.4s ease",
          marginTop: open ? 12 : 0,
        }}>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, lineHeight:1.6, color:"rgba(255,255,255,0.72)", margin:0 }}>
            {a}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

/* DATA */
const D = {
  stats: [
    { val: '80%', unit: 'of ISVs', label: 'Struggle with billing complexity as they scale across channels' },
    { val: '3x',  unit: '',        label: 'Faster partner onboarding with automated ISV workflows' },
    { val: '45%', unit: '',        label: 'Revenue leakage reduction from automated usage reconciliation' },
  ],
  problems: [
    { icon: 'credit',  title: 'Fragmented billing models',      desc: 'ISVs juggle per-seat, usage-based, and hybrid pricing across channels — each billing differently with no unified engine to reconcile it.' },
    { icon: 'clock',   title: 'Slow channel partner activation', desc: 'Onboarding resellers and distributors manually stalls GTM velocity and delays revenue from partner-sourced deals.' },
    { icon: 'alert',   title: 'Revenue recognition gaps',       desc: 'Disconnected quoting, provisioning, and billing systems create errors that delay close and inflate DSO.' },
    { icon: 'network', title: 'No multi-tier visibility',        desc: 'Without a unified view across direct, reseller, and marketplace channels, commercial decisions are made on incomplete data.' },
  ],
  comparison: [
    { f: 'Billing model support',     ours: 'Per-seat, usage, hybrid — all in one engine',  theirs: 'Single model, requires custom dev for others' },
    { f: 'Channel partner portal',    ours: 'White-label self-serve portal included',         theirs: 'No native portal — built separately' },
    { f: 'Usage metering',            ours: 'Real-time metering with automated reconciliation', theirs: 'Manual exports and spreadsheet matching' },
    { f: 'Marketplace integrations',  ours: 'AWS, Azure, GCP marketplaces out of the box',   theirs: 'Custom integration required per marketplace' },
    { f: 'Revenue analytics',         ours: 'MRR, ARR, churn, NRR — live dashboards',        theirs: 'Exported reports, delayed by days' },
  ],
  whyFeatures: [
    'Support per-seat, usage-based, and hybrid billing from one platform without custom engineering',
    'Onboard channel partners with a white-label self-serve portal in days, not weeks',
    'Publish directly to AWS, Azure, and GCP marketplaces without duplicating your catalog',
    'Automate renewals, dunning, and expansion revenue flows end to end',
    'Get full commercial visibility across direct, reseller, and marketplace channels in real time',
  ],
  pillars: [
    { icon: 'dollar',  title: 'Flexible Billing Engine',     desc: 'One engine to handle every pricing model — per-seat, usage-based, tiered, hybrid — with automated reconciliation.' },
    { icon: 'users',   title: 'Channel Partner Management',  desc: 'Onboard, tier, and manage resellers with a white-label portal, margin controls, and co-sell automation.' },
    { icon: 'network', title: 'Marketplace Distribution',    desc: 'List and transact on AWS, Azure, and GCP marketplaces without duplicating catalog work or billing logic.' },
  ],
  features: [
    { icon: 'dollar',  title: 'Multi-Model Billing',          desc: 'Seat-based, consumption, prepaid credits, and hybrid — all in one billing engine with no custom code.' },
    { icon: 'refresh', title: 'Renewal & Expansion Automation', desc: 'Automated renewal workflows, dunning sequences, and upsell triggers reduce churn and grow NRR.' },
    { icon: 'eye',     title: 'Usage Metering & Rating',      desc: 'Real-time metering ingests usage events, applies your pricing logic, and generates invoice-ready outputs.' },
    { icon: 'layers',  title: 'Channel Partner Portal',       desc: 'White-labeled, self-serve reseller portal with deal registration, margin visibility, and co-sell tools.' },
    { icon: 'globe',   title: 'Marketplace Publishing',       desc: 'Publish listings to AWS, Azure, and GCP from one catalog. Transactions flow back into your billing engine.' },
    { icon: 'bar',     title: 'Revenue Intelligence',         desc: 'Live dashboards for MRR, ARR, churn, NRR, and partner contribution — segmented by channel and product.' },
  ],
  aiTags: [
    'Predictive churn scoring',
    'Expansion revenue signals',
    'Dynamic pricing recommendations',
    'Billing anomaly detection',
    'Partner propensity modeling',
    'Usage trend forecasting',
    'Renewal risk alerts',
    'Natural language catalog search',
  ],
  platformFeatures: [
    { val: '10 days', label: 'Time to go live',       desc: 'Full implementation including catalog, billing, and partner portal setup.' },
    { val: '99.9%',   label: 'Platform uptime SLA',   desc: 'SOC 2 Type II and ISO 27001 certified with 24/7 enterprise support.' },
    { val: '40+',     label: 'Marketplace connectors', desc: 'Native integrations to AWS, Azure, GCP, Salesforce, HubSpot, and leading billing tools.' },
    { val: '3x',      label: 'Faster partner GTM',    desc: 'Automated onboarding cuts activation time from weeks to days.' },
  ],
  results: [
    { val: '45%',  label: 'Revenue leakage reduction after switching to automated usage billing', org: 'Series B SaaS ISV, EMEA market' },
    { val: '3x',   label: 'Faster reseller onboarding with white-label partner portal',           org: 'Cloud infrastructure ISV, North America' },
    { val: '60%',  label: 'Reduction in billing ops headcount after full automation rollout',     org: 'Mid-market ISV, APAC region' },
  ],
  faq: [
    {
      q: 'Does AppGallop support usage-based billing for ISVs?',
      a: 'Yes. The billing engine ingests raw usage events, applies your pricing rules — tiered, committed, overage, credits — and outputs invoice-ready data. No custom metering infrastructure needed.',
    },
    {
      q: 'Can we use AppGallop to sell through AWS and Azure Marketplace?',
      a: 'Yes. AppGallop has native connectors to AWS, Azure, and GCP marketplaces. You publish once from your catalog and transactions flow back into your billing and revenue reporting automatically.',
    },
    {
      q: 'How does the channel partner portal work for our resellers?',
      a: 'Resellers get a fully white-labeled, self-serve portal under your brand and domain. They can register deals, view their margins, manage customers, and track performance — without ever seeing AppGallop.',
    },
    {
      q: 'How long does implementation take for an ISV with an existing catalog?',
      a: 'Most ISVs are fully live within 10 business days. Our team handles catalog migration, billing model configuration, and partner portal setup end to end.',
    },
  ],
};

/* PAGE */
export default function ISVPage() {
  return (
    <div className="bg-white overflow-x-hidden">
      <style>{`
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(40px,-30px) scale(1.08)} 66%{transform:translate(-25px,20px) scale(0.95)} }
        @keyframes pulse3d  { 0%,100%{box-shadow:0 0 0 0 rgba(227,30,36,0.4)} 50%{box-shadow:0 0 0 20px rgba(227,30,36,0)} }
        .card-3d { transition:transform 0.3s ease,box-shadow 0.3s ease }
        .card-3d:hover { transform:translateY(-8px);box-shadow:0 32px 64px rgba(0,0,0,0.18) }
      `}</style>

      {/* HERO */}
      <section className="relative h-screen flex flex-col justify-center bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden px-6" style={{ paddingTop:'80px', paddingBottom:'40px' }}>
        <ParticleCanvas count={60} color="#ff5d22" />
        <GridFloor opacity={0.03} />
        <Orb style={{ top:'-5%', right:'-8%' }} color="#ff5d22" size={400} animDur={18} />
        <Orb style={{ bottom:'-10%', left:'-5%' }} color="#3b82f6" size={350} animDur={22} animDelay={3} />

        <div className="relative z-20 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/6 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-4 border border-slate-200">
                <svg width={8} height={8} viewBox="0 0 10 10"><circle cx={5} cy={5} r={5} fill="#ff5d22" /></svg>
                ISV Distribution Platform
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.1] text-slate-950 mb-4">
                The billing and channel platform{' '}
                <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                  built for ISVs that scale
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-sm text-slate-600 leading-relaxed mb-6 max-w-lg font-medium">
                Independent software vendors lose revenue to billing complexity, slow partner activation, and fragmented marketplace operations. AppGallop unifies every commercial motion into one platform.
              </p>
            </Reveal>
            <Reveal delay={0.20}>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <button className="px-6 py-2.5 rounded-lg bg-[#ff5d22] text-white font-black text-[10px] uppercase tracking-widest border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300" style={{ animation:'pulse3d 3s ease infinite' }}>
                    Book a Demo
                  </button>
                </Link>
                <Link to="/solutions">
                  <button className="px-6 py-2.5 rounded-lg bg-white text-slate-700 font-black text-[10px] uppercase tracking-widest border border-slate-300 cursor-pointer hover:bg-slate-50 transition-all duration-300">
                    Explore Platform
                  </button>
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Stats cluster */}
          <div className="grid gap-2.5">
            {D.stats.map((s, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <TiltCard className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" style={{ position:'relative' }}>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="text-3xl font-black text-[#2563EB] leading-none">{s.val}</div>
                      <div className="text-[8px] text-slate-400 font-black uppercase tracking-wider mt-0.5">{s.unit || 'metric'}</div>
                    </div>
                    <div className="w-px h-8 bg-slate-200" />
                    <div className="text-xs text-slate-600 font-medium leading-relaxed">{s.label}</div>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEMS (dark) */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas count={50} color="#ff5d22" />
        <Orb style={{ top:'15%', right:'-8%' }} color="#ff5d22" size={350} animDur={20} />
        <div className="relative z-20 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                Why ISVs Struggle to Scale
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Billing complexity is the{' '}
                <span className="text-[#ff5d22]">silent growth killer</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-sm text-white/50 max-w-xl mx-auto leading-relaxed font-medium">
                As ISVs expand channels, pricing models, and geographies, the operational debt from disconnected billing and partner systems compounds — slowing revenue and frustrating customers.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {D.problems.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard className="bg-white/3 border border-white/8 rounded-lg p-6 backdrop-blur-xl relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                  <div className="w-10 h-10 rounded-lg bg-[#ff5d22]/15 border border-[#ff5d22]/30 flex items-center justify-center mb-3">
                    <Icon d={icons[item.icon]} size={18} style={{ color:'#ff5d22' }} />
                  </div>
                  <h3 className="text-base font-black text-white mb-2 tracking-tight">{item.title}</h3>
                  <p className="text-xs text-white/55 leading-relaxed font-medium">{item.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON (light) */}
      <section className="relative bg-slate-50 py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.03)_1px,transparent_1px)] bg-[length:30px_30px]" />
        <Orb style={{ top:'10%', right:'5%' }} color="#ff5d22" size={280} animDur={18} />
        <div className="relative z-20 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                AppGallop vs Generic Billing Tools
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                Purpose-built for ISV distribution —{' '}
                <span className="text-[#ff5d22]">not patched together</span>
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Generic */}
            <Reveal delay={0.1}>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Generic Billing / Channel Tools</span>
                </div>
                {D.comparison.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between py-2.5 text-xs ${i < D.comparison.length - 1 ? 'border-b border-slate-50' : ''}`}>
                    <span className="font-bold text-slate-700">{c.f}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
                        <Icon d={icons.x} size={8} style={{ color:'#ef4444' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-400 font-bold text-[10px] max-w-[140px] text-right leading-tight">{c.theirs}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            {/* AppGallop */}
            <Reveal delay={0.16}>
              <div className="bg-gradient-to-br from-slate-950 to-slate-900 border border-[#ff5d22]/30 rounded-2xl p-6 relative overflow-hidden shadow-lg">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22] to-transparent" />
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/8">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#ff5d22] shadow-lg shadow-[#ff5d22]/50" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-white/60">AppGallop</span>
                  </div>
                  <span className="text-[8px] font-black uppercase tracking-widest text-[#ff5d22] flex items-center gap-1">
                    <Icon d={icons.zap} size={8} style={{ color:'#ff5d22' }} /> Recommended
                  </span>
                </div>
                {D.comparison.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between py-2.5 text-xs ${i < D.comparison.length - 1 ? 'border-b border-white/6' : ''}`}>
                    <span className="font-bold text-white/85">{c.f}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-[#ff5d22]/20 flex items-center justify-center">
                        <Icon d={icons.check} size={8} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-400 font-bold text-[10px] max-w-[140px] text-right leading-tight">{c.ours}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY US (white, 2-col) */}
      <section style={{ position:'relative', background:'#fff', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ bottom:'0%', left:'0%' }} color="#ff5d22" size={350} animDur={22} />
        <div style={{ position:'relative', zIndex:2, maxWidth:1000, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'center' }}>
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Purpose-Built for ISVs
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-4">
                One platform for every{' '}
                <span className="text-[#ff5d22]">commercial motion</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:15, color:'#6b7280', lineHeight:1.75 }}>
                Most billing and channel tools solve one piece of the puzzle. AppGallop connects billing, channel, and marketplace distribution in one platform — so your commercial operations scale without scaling your ops team.
              </p>
            </Reveal>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {D.whyFeatures.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d" style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 1px 8px rgba(0,0,0,0.04)', cursor:'default' }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:'rgba(227,30,36,0.08)', border:'1px solid rgba(227,30,36,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Icon d={icons.check} size={14} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                  </div>
                  <span style={{ fontSize:13, fontWeight:600, color:'#1f2937', lineHeight:1.5 }}>{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS (dark, 3-col) */}
      <section style={{ position:'relative', background:'#08080c', padding:'80px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={50} color="#3b82f6" />
        <Orb style={{ top:'5%', left:'15%' }} color="#ff5d22" size={400} animDur={20} />
        <GridFloor opacity={0.04} />
        <div style={{ position:'relative', zIndex:2, maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                Three Pillars of ISV Revenue
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Billing. Channels. <span className="text-[#ff5d22]">Marketplaces.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', maxWidth:520, margin:'0 auto', lineHeight:1.7 }}>
                Three revenue-critical operations, unified. Stop stitching together point solutions and manage your entire commercial model from one platform.
              </p>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }}>
            {D.pillars.map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16, padding:'32px 24px', textAlign:'center', backdropFilter:'blur(16px)', position:'relative', overflow:'hidden', height:'100%' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,rgba(227,30,36,${0.3 + i * 0.15}),transparent)` }} />
                  <div style={{ position:'relative', display:'inline-block', marginBottom:20 }}>
                    <div style={{ position:'absolute', inset:-10, borderRadius:'50%', background:'rgba(227,30,36,0.12)', filter:'blur(12px)' }} />
                    <div style={{ position:'relative', width:56, height:56, borderRadius:14, background:'rgba(227,30,36,0.15)', border:'1px solid rgba(227,30,36,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon d={icons[p.icon]} size={22} style={{ color:'#ff5d22' }} />
                    </div>
                  </div>
                  <h3 style={{ fontSize:17, fontWeight:800, color:'#fff', marginBottom:10 }}>{p.title}</h3>
                  <p style={{ fontSize:13, color:'rgba(255,255,255,0.55)', lineHeight:1.7, margin:0 }}>{p.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GTM BANNER (red) */}
      <section style={{ position:'relative', background:'linear-gradient(135deg,#ff5d22 0%,#b91c1c 50%,#991b1b 100%)', padding:'60px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(255,255,255,0.08) 1px,transparent 1px)', backgroundSize:'24px 24px' }} />
        <div style={{ position:'relative', zIndex:2, maxWidth:800, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <span style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 12px', borderRadius:100, background:'rgba(255,255,255,0.15)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', fontSize:9, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase' as const, marginBottom:16 }}>
              Sell Everywhere. Bill Once. Grow Faster.
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4">
              Stop Rebuilding Billing Every Time You Add a Channel
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.85)', lineHeight:1.75, maxWidth:580, margin:'0 auto' }}>
              One billing engine. Every pricing model. Every channel. AppGallop removes the operational ceiling that holds ISVs back from scaling direct, partner, and marketplace revenue simultaneously.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURES GRID (#fafafa) */}
      <section style={{ position:'relative', background:'#fafafa', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <div style={{ position:'relative', zIndex:2, maxWidth:1200, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Features
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                The Features That Make ISVs <span className="text-[#ff5d22]">Competitive</span>
              </h2>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {D.features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d" style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:'24px 20px', position:'relative', overflow:'hidden', height:'100%', boxShadow:'0 1px 12px rgba(0,0,0,0.03)' }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:'rgba(227,30,36,0.07)', border:'1px solid rgba(227,30,36,0.15)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
                    <Icon d={icons[f.icon]} size={18} style={{ color:'#ff5d22' }} />
                  </div>
                  <h3 style={{ fontSize:15, fontWeight:800, color:'#111827', marginBottom:8 }}>{f.title}</h3>
                  <p style={{ fontSize:13, color:'#6b7280', lineHeight:1.65, margin:0 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI CAPABILITIES (dark) */}
      <section style={{ position:'relative', background:'#0a0a0e', padding:'80px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={50} color="#8b5cf6" />
        <Orb style={{ top:'-5%', right:'-8%' }} color="#ff5d22" size={400} animDur={19} />
        <div style={{ position:'relative', zIndex:2, maxWidth:840, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              AI Capabilities
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              AI that grows your <span className="text-[#ff5d22]">NRR</span>, not just your spreadsheets
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.55)', lineHeight:1.75, maxWidth:580, margin:'0 auto 40px' }}>
              AppGallop's AI layer identifies expansion opportunities, flags at-risk accounts, and surfaces pricing signals — so your commercial team acts on intelligence, not instinct.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ display:'flex', justifyContent:'center', gap:10, flexWrap:'wrap' }}>
              {D.aiTags.map((tag, i) => (
                <span key={i} style={{ padding:'8px 16px', borderRadius:100, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:600 }}>
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLARITY (modern split-screen) */}
      <section className="relative py-16 bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
        <ParticleCanvas count={40} color="#ff5d22" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.02)_1px,transparent_1px)] bg-[length:40px_40px]" />
        <Orb style={{ top:'10%', right:'-5%' }} color="#ff5d22" size={300} animDur={20} />
        <Orb style={{ bottom:'15%', left:'-8%' }} color="#3b82f6" size={250} animDur={25} animDelay={2} />
        
        <div className="relative z-20 max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Side - Problem Statement */}
            <div className="space-y-6">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs font-bold uppercase tracking-wider">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  The Problem
                </div>
              </Reveal>
              
              <Reveal delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-slate-900">
                  ISV Billing Is{' '}
                  <span className="relative">
                    <span className="text-red-600">Broken</span>
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-red-200 rounded-full" />
                  </span>
                </h2>
              </Reveal>
              
              <Reveal delay={0.2}>
                <div className="space-y-3">
                  {[
                    'Multiple billing systems that don\'t communicate',
                    'Manual reconciliation consuming 40+ hours monthly',
                    'Revenue leakage from disconnected usage tracking',
                    'Partner portals built with temporary solutions'
                  ].map((problem, i) => (
                    <TiltCard key={i} className="flex items-start gap-3 p-3 bg-white/60 backdrop-blur-sm border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon d={icons.x} size={10} style={{ color:'#dc2626' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-sm font-medium text-slate-700 leading-relaxed">{problem}</span>
                    </TiltCard>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Right Side - Solution */}
            <div className="space-y-6">
              <Reveal delay={0.3}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs font-bold uppercase tracking-wider">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  The Solution
                </div>
              </Reveal>
              
              <Reveal delay={0.4}>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-slate-900">
                  AppGallop{' '}
                  <span className="relative">
                    <span className="text-[#ff5d22]">Fixes It</span>
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#ff5d22]/30 rounded-full" />
                  </span>
                </h2>
              </Reveal>
              
              <Reveal delay={0.5}>
                <div className="space-y-3">
                  {[
                    'One unified platform for all billing models',
                    'Automated reconciliation saves 35+ hours monthly',
                    'Real-time usage tracking prevents revenue leakage',
                    'White-label partner portals deployed in days'
                  ].map((solution, i) => (
                    <TiltCard key={i} className="flex items-start gap-3 p-3 bg-gradient-to-r from-white to-slate-50 border border-[#ff5d22]/20 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300">
                      <div className="w-5 h-5 rounded-full bg-[#ff5d22]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon d={icons.check} size={10} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-sm font-medium text-slate-700 leading-relaxed">{solution}</span>
                    </TiltCard>
                  ))}
                </div>
              </Reveal>
              
              <Reveal delay={0.6}>
                <div className="pt-4">
                  <Link to="/contact">
                    <button className="group px-6 py-3 rounded-lg bg-[#ff5d22] text-white font-bold text-sm uppercase tracking-wider border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2">
                      See It In Action
                      <Icon d={icons.arrow} size={14} style={{ color:'#fff' }} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
          
          {/* Bottom Stats Bar */}
          <Reveal delay={0.7}>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { metric: '10 Days', label: 'Average implementation time' },
                { metric: '99.9%', label: 'Billing accuracy after go-live' },
                { metric: '40+ Hours', label: 'Saved per month on reconciliation' }
              ].map((stat, i) => (
                <TiltCard key={i} className="text-center p-4 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="text-2xl font-black text-[#2563EB] mb-1">{stat.metric}</div>
                  <div className="text-xs font-medium text-slate-600 uppercase tracking-wider">{stat.label}</div>
                </TiltCard>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CASE STUDY (#fafafa) */}
      <section style={{ position:'relative', background:'#fafafa', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <div style={{ position:'relative', zIndex:2, maxWidth:920, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Proven Results
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                ISVs Scaling Faster With <span className="text-[#ff5d22]">AppGallop</span>
              </h2>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
            {D.results.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:'32px 24px', textAlign:'center', boxShadow:'0 2px 16px rgba(0,0,0,0.04)', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,#ff5d22,rgba(227,30,36,0.3))' }} />
                  <div style={{ fontSize:48, fontWeight:900, color:'#2563EB', lineHeight:1, marginBottom:8 }}>{m.val}</div>
                  <div style={{ fontSize:12, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase' as const, color:'#9ca3af', marginBottom:8 }}>{m.label}</div>
                  <div style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>{m.org}</div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div style={{ background:'linear-gradient(135deg,#111,#1c1c1c)', borderRadius:16, padding:'36px 32px', border:'1px solid rgba(255,255,255,0.08)', position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,rgba(227,30,36,0.6),transparent)' }} />
              <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'rgba(227,30,36,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                  <Icon d={icons.bar} size={18} style={{ color:'#ff5d22' }} />
                </div>
                <p style={{ fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.75, margin:0 }}>
                  A Series B SaaS ISV with three pricing models and 200+ resellers consolidated their billing, partner portal, and marketplace operations onto AppGallop. Within 60 days, billing disputes dropped to near zero, their AWS Marketplace listing was live with automatic transaction reconciliation, and the finance team cut monthly billing close from 4 days to 6 hours.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ACTIVATION (dark) */}
      <section style={{ position:'relative', background:'#08080c', padding:'80px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={70} />
        <Orb style={{ top:'5%', left:'10%' }} color="#ff5d22" size={450} animDur={20} />
        <GridFloor opacity={0.06} />
        <div style={{ position:'relative', zIndex:2, maxWidth:800, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              Your ISV Revenue Engine, Activated
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              The Platform Behind <span className="text-[#ff5d22]">Fast-Growing ISVs</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.55)', lineHeight:1.75, maxWidth:580, margin:'0 auto 32px' }}>
              Join ISVs who have replaced fragile billing stacks and disconnected partner portals with one platform built for multi-channel revenue at scale.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact">
              <button style={{ padding:'12px 32px', borderRadius:10, background:'#ff5d22', color:'#fff', fontWeight:700, fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase' as const, border:'none', cursor:'pointer', animation:'pulse3d 3s ease infinite', display:'inline-flex', alignItems:'center', gap:8 }}>
                Book a Demo
                <Icon d={icons.arrow} size={14} style={{ color:'#fff' }} strokeWidth={2} />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PLATFORM METRICS (white) */}
      <section style={{ position:'relative', background:'#fff', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ bottom:'0%', right:'0%' }} color="#ff5d22" size={300} animDur={21} />
        <div style={{ position:'relative', zIndex:2, maxWidth:920, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Metrics
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-3">
                Built for ISVs Who Need <span className="text-[#ff5d22]">Speed and Scale</span>
              </h2>
            </Reveal>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:32 }}>
            {D.platformFeatures.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:'24px 20px', textAlign:'center', boxShadow:'0 1px 12px rgba(0,0,0,0.03)', position:'relative', overflow:'hidden', height:'100%' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,#ff5d22,rgba(227,30,36,0.2))' }} />
                  <div style={{ fontSize:32, fontWeight:900, color:'#2563EB', lineHeight:1, marginBottom:6 }}>{item.val}</div>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' as const, color:'#9ca3af', marginBottom:8 }}>{item.label}</div>
                  <p style={{ fontSize:12, color:'#6b7280', lineHeight:1.6, margin:0 }}>{item.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div style={{ background:'linear-gradient(135deg,#111,#1c1c1c)', borderRadius:16, padding:'40px 36px', textAlign:'center', border:'1px solid rgba(255,255,255,0.06)', position:'relative', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,#ff5d22,transparent)' }} />
              <div style={{ width:48, height:48, borderRadius:12, background:'rgba(227,30,36,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <Icon d={icons.msg} size={20} style={{ color:'#ff5d22' }} />
              </div>
              <blockquote style={{ fontSize:18, fontStyle:'italic', color:'rgba(255,255,255,0.85)', fontWeight:400, lineHeight:1.65, maxWidth:520, margin:'0 auto 16px' }}>
                "Before AppGallop, billing close took our finance team four days every month. Now it takes four hours — and we have zero disputes."
              </blockquote>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' as const, color:'rgba(255,255,255,0.35)' }}>
                VP Finance, Series B SaaS ISV
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ position:'relative', background:'#fafafa', padding:'80px 24px', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <Orb style={{ top:'15%', left:'-8%' }} color="#ff5d22" size={280} animDur={19} />
        <div style={{ position:'relative', zIndex:2, maxWidth:700, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                FAQ
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 style={{ fontSize:'clamp(28px,4vw,48px)', fontWeight:800, marginBottom:0, color:'#1f2937' }}>
                Frequently Asked Questions
              </h2>
            </Reveal>
          </div>
          {D.faq.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} idx={i} />
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ position:'relative', background:'#06060a', padding:'100px 24px', overflow:'hidden' }}>
        <ParticleCanvas count={90} />
        <GridFloor opacity={0.06} />
        <Orb style={{ top:'10%', left:'10%' }} color="#ff5d22" size={450} animDur={18} />
        <Orb style={{ bottom:'10%', right:'5%' }} color="#3b82f6" size={380} animDur={22} animDelay={4} />
        <div style={{ position:'relative', zIndex:2, maxWidth:780, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase' as const, color:'rgba(255,255,255,0.4)', marginBottom:16 }}>
              Ready to Scale?
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 style={{ fontSize:'clamp(32px,5vw,64px)', color:'#fff', fontWeight:800, marginBottom:16, lineHeight:1.05 }}>
              Turn Your Billing Stack Into a{' '}
              <span style={{ color:'#ff5d22' }}>Revenue Engine</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.55)', lineHeight:1.75, maxWidth:500, margin:'0 auto 32px' }}>
              See how AppGallop unifies your billing, channels, and marketplace distribution in one platform — and go live in 10 days.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
              <Link to="/contact">
                <button style={{ padding:'12px 32px', borderRadius:10, background:'#ff5d22', color:'#fff', fontWeight:700, fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase' as const, border:'none', cursor:'pointer', animation:'pulse3d 3s ease infinite', display:'inline-flex', alignItems:'center', gap:8 }}>
                  Book a Demo
                  <Icon d={icons.arrow} size={14} style={{ color:'#fff' }} strokeWidth={2} />
                </button>
              </Link>
              <Link to="/solutions">
                <button style={{ padding:'12px 32px', borderRadius:10, background:'transparent', color:'rgba(255,255,255,0.75)', fontWeight:700, fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase' as const, border:'1px solid rgba(255,255,255,0.18)', cursor:'pointer' }}>
                  Explore Solutions
                </button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ContactSection id="contact" />
    </div>
  );
}
